import type {
  HardwareRequestApprovalStatus,
  HardwareRequestReplacedAsset,
  HardwareRequestSummary,
} from '@/types/oscar';

/**
 * Derived readings for the hardware request registry (Phase 1).
 *
 * Kept out of the components on purpose: these are the judgements an approver
 * acts on, and every one of them has an "we do not know" case that is easy to
 * collapse into a confident wrong answer. They are easier to argue about — and
 * to test — as functions than as JSX.
 */

/** How a derived reading should be coloured. */
export type Tone = 'ok' | 'warn' | 'bad' | 'unknown';

/**
 * Age of a device in words.
 *
 * ⛔ `null` is NOT zero. A device with no recorded purchase date must never
 * render as brand new — that is the reading most likely to get a fair
 * replacement refused.
 */
export function ageLabel(months: number | null | undefined): string {
  if (months == null) return 'Not recorded';
  if (months < 0) return 'Not recorded';
  if (months === 0) return 'Under a month';
  if (months < 12) return `${months} month${months === 1 ? '' : 's'}`;

  const years = Math.floor(months / 12);
  const rest = months % 12;
  const y = `${years} year${years === 1 ? '' : 's'}`;
  return rest === 0 ? y : `${y} ${rest} month${rest === 1 ? '' : 's'}`;
}

/**
 * Whether the age alone suggests replacement is reasonable.
 *
 * ⚠ THREE YEARS IS A PLACEHOLDER, NOT A POLICY. Onsite has no agreed
 * replacement cycle recorded anywhere this code can read, so this colours the
 * number rather than deciding anything, and the UI must show the age itself
 * beside it. When a real cycle exists it belongs in configuration, not here.
 */
export const ASSUMED_REPLACEMENT_CYCLE_MONTHS = 36;

export function ageTone(months: number | null | undefined): Tone {
  if (months == null || months < 0) return 'unknown';
  if (months >= ASSUMED_REPLACEMENT_CYCLE_MONTHS) return 'ok';
  if (months >= ASSUMED_REPLACEMENT_CYCLE_MONTHS - 6) return 'warn';
  return 'bad';
}

/**
 * Warranty, as three states rather than two.
 *
 * ⛔ "We do not know when the warranty expires" and "the warranty has expired"
 * are different answers and only one of them justifies buying a new device. The
 * API returns `IsInWarranty` as a tri-state for exactly this reason; collapsing
 * null to false here would throw that away at the last step.
 */
export function warrantyReading(
  asset: Pick<HardwareRequestReplacedAsset, 'IsInWarranty' | 'WarrantyExpiry' | 'DellWarrantyStatus'>,
): { label: string; tone: Tone } {
  if (asset.IsInWarranty === null || asset.IsInWarranty === undefined) {
    return { label: 'Warranty not recorded', tone: 'unknown' };
  }
  return asset.IsInWarranty
    ? { label: 'In warranty — consider repair', tone: 'bad' }
    : { label: 'Out of warranty', tone: 'ok' };
}

/** Whole days between an ISO timestamp and now. Null when absent or unparseable. */
export function daysSince(value: string | null | undefined, now: Date = new Date()): number | null {
  if (!value) return null;
  const then = new Date(value);
  if (Number.isNaN(then.getTime())) return null;
  return Math.floor((now.getTime() - then.getTime()) / 86_400_000);
}

/**
 * Intune check-in, in words. A device that stopped checking in months ago is a
 * materially different case from one that checked in this morning, and neither
 * has ever been visible at decision time.
 */
export function checkInReading(
  value: string | null | undefined,
  now: Date = new Date(),
): { label: string; tone: Tone } {
  const days = daysSince(value, now);
  if (days === null) return { label: 'Never seen in Intune', tone: 'unknown' };
  if (days <= 7) return { label: days <= 1 ? 'Checked in today' : `Checked in ${days} days ago`, tone: 'ok' };
  if (days <= 30) return { label: `Checked in ${days} days ago`, tone: 'warn' };
  return { label: `Not seen for ${days} days`, tone: 'bad' };
}

/**
 * Did the manage → oscar lifecycle write reach this asset?
 *
 * The snapshot (taken at submit) and the live state are ALLOWED to disagree —
 * migration 069 says so — and the disagreement is the evidence.
 */
export function lifecycleReading(
  asset: Pick<HardwareRequestReplacedAsset, 'OscarUpdatedAt' | 'OscarUpdateError' | 'CurrentLifecycleState' | 'SnapshotPriorState' | 'AssetMissingFromOscar'>,
): { label: string; tone: Tone } {
  if (asset.AssetMissingFromOscar) {
    return { label: 'Oscar no longer holds this asset', tone: 'unknown' };
  }
  if (asset.OscarUpdateError) {
    return { label: 'Oscar was not updated — see the error', tone: 'bad' };
  }
  if (asset.OscarUpdatedAt) {
    return { label: `Marked as replacement requested (was ${asset.SnapshotPriorState ?? 'unknown'})`, tone: 'ok' };
  }
  return { label: 'Oscar update not attempted', tone: 'warn' };
}

/**
 * What the approver is actually looking at, for the replaced-device panel.
 *
 * ⛔ THE `notListed` CASE IS THE ONE THAT MATTERS. Since Rev 8.1 a replacement
 * can legitimately carry NO asset, because the requester ticked
 * "Other — it is not listed, or I would rather not say". That is a DELIBERATE
 * answer, and the panel must say so.
 *
 * Drawing it as "no devices recorded" would read as missing data and invite an
 * approver to bounce the request back for something the form already asked and
 * the requester already answered.
 */
export type ReplacementEvidence =
  | { kind: 'notARequest' }
  | { kind: 'notListed' }
  | { kind: 'assets'; assets: HardwareRequestReplacedAsset[] };

export function replacementEvidence(
  request: Pick<HardwareRequestSummary, 'RequestType'>,
  assets: HardwareRequestReplacedAsset[],
): ReplacementEvidence {
  if (request.RequestType !== 'Replacement') return { kind: 'notARequest' };
  if (assets.length === 0) return { kind: 'notListed' };
  return { kind: 'assets', assets };
}

/**
 * How the request reached Support.
 *
 * ⛔ "No Zendesk ticket id" does NOT mean no ticket. The API path writes an id;
 * the SMTP2GO email fallback does not, and a ticket raised from inbound mail
 * leaves this null. Presenting that as "not raised" would be the same mistake
 * that read an email-built ticket as proof the API worked on 17/09/2026.
 */
export function deliveryReading(
  request: Pick<HardwareRequestSummary, 'ZendeskTicketId' | 'ZendeskError' | 'EmailSentAt' | 'EmailError'>,
): { label: string; tone: Tone } {
  if (request.ZendeskTicketId) {
    return { label: `Zendesk #${request.ZendeskTicketId}`, tone: 'ok' };
  }
  if (request.ZendeskError) {
    return { label: 'Zendesk API failed', tone: request.EmailSentAt ? 'warn' : 'bad' };
  }
  if (request.EmailSentAt) {
    return { label: 'Emailed to Support (no API ticket id)', tone: 'warn' };
  }
  if (request.EmailError) {
    return { label: 'Not delivered — email failed', tone: 'bad' };
  }
  return { label: 'No delivery recorded', tone: 'bad' };
}

/**
 * Tailwind classes for a {@link Tone} badge, in Oscar's palette.
 *
 * Shared by the list and the detail so the same reading cannot be coloured two
 * different ways on two screens. ⚠ `unknown` is deliberately grey rather than
 * amber: "we have no data" is not a warning about the device, it is a warning
 * about our records, and colouring it as a problem with the request would push
 * an approver towards the wrong conclusion.
 */
export const toneClass: Record<Tone, string> = {
  ok: 'bg-green/10 text-green',
  warn: 'bg-orange/10 text-orange',
  bad: 'bg-red/10 text-red',
  unknown: 'bg-bg text-muted',
};

/**
 * How a decision should read on screen.
 *
 * ⚠ Pending is **unknown**, not a warning. A request nobody has looked at yet
 * is the normal state of a new request; colouring it amber makes an ordinary
 * queue look like a list of problems, and a screen that always looks alarmed
 * stops being read.
 *
 * ⛔ Referred is amber rather than red because it is **not a refusal** — the
 * move is back with the requester, and the request is still alive. Drawing it
 * the same as Declined would tell somebody their request was rejected when it
 * was only queried.
 */
export function decisionReading(
  status: HardwareRequestApprovalStatus,
): { label: string; tone: Tone } {
  switch (status) {
    case 'Approved':
      return { label: 'Approved', tone: 'ok' };
    case 'Declined':
      return { label: 'Declined', tone: 'bad' };
    case 'Referred':
      return { label: 'Referred back', tone: 'warn' };
    default:
      return { label: 'Awaiting decision', tone: 'unknown' };
  }
}

/**
 * Whether a decision may be recorded right now.
 *
 * Only one live decision per request — the database enforces it with a filtered
 * unique index, and this is what stops the UI offering a second one and getting
 * a 409 back. Undoing (admin) is what reopens it.
 */
export function canRecordDecision(status: HardwareRequestApprovalStatus): boolean {
  return status === 'Pending';
}

/** Who a request is for (060). The strings match the database CHECK constraint. */
export const BENEFICIARY_BRANCH = 'Branch';

/**
 * Is this request for a branch rather than a person (060)?
 *
 * ⛔ Every screen that renders `EmployeeName` must ask this first. The name is
 * always present and always looks like a name, so a branch request rendered
 * without it reads as somebody having typed a place into the wrong field —
 * and an approver who believes that will go looking for a person's assets.
 *
 * ⚠ Tolerates a missing value. This code can be deployed before oscar
 * migration 073 is applied, and "no kind" must mean "a person", never "a
 * branch" — the safe direction, since it only loses a badge.
 */
export function isForBranch(kind: string | null | undefined): boolean {
  return kind === BENEFICIARY_BRANCH;
}
